import { useEffect, useState } from "react";
import { openJobQuickView, setPage, toPrettyDate, useHub } from "../../../../core/Global";
import { API_ROUTES } from "../../../../core/Constants";
import { DataTable, DataTableCustomActions, DataTableField } from "../../../../components/data/DataTable";
import Icon from "../../../../components/base/Icon";
import FlexRow from "../../../../components/base/FlexRow";
import Indicator from "../../../../components/base/Indicator";
import Modal from "../../../../components/base/Modal";
import Button from "../../../../components/base/Button";
import { Drawer } from "../../../../components/drawers/Drawer";
import CallConfirmModal, { usePhoneReady } from "../../../../components/phone/CallConfirmModal";
import PhoneItem from "../../../../models/PhoneItem";
import Job from "../models/Job";
import JobInvoice from "../models/JobInvoice";
import JobInvoiceDetails from "./JobInvoiceDetails";
import JobInvoiceCreateDrawer from "./JobInvoiceCreateDrawer";
import JobInvoiceSendDrawer from "../components/JobInvoiceSendDrawer";
import filteringProps from "../filtering/jobinvoices.json";

const moneyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
});

export default function JobInvoicesList() {

    const hub = useHub();

    useEffect(() => {
        setPage("Job Invoices", "CRM", "Jobs");
    }, []);

    const [invoiceDrawer, invoiceDrawerSet] = useState<boolean>(false);
    const [selectedRow, selectedRowSet] = useState<JobInvoice>();
    const [drawerJob, drawerJobSet] = useState<Job>();
    const [jobLoading, jobLoadingSet] = useState<boolean>(false);

    const [refresh, refreshSet] = useState<boolean>();
    useEffect(() => {
        if (refresh) {
            setTimeout(() => refreshSet(false), 1000);
        }
    }, [refresh]);

    // Create New drawer
    const [createDrawer, createDrawerSet] = useState<boolean>(false);

    // Row-action modals
    const [actionRow, actionRowSet] = useState<JobInvoice>();
    const [sendModal, sendModalSet] = useState<boolean>(false);
    const [confirmModal, confirmModalSet] = useState<boolean>(false);
    const [undoConfirmModal, undoConfirmModalSet] = useState<boolean>(false);
    const [estToInvModal, estToInvModalSet] = useState<boolean>(false);

    const phoneReady = usePhoneReady();
    const [callNumber, callNumberSet] = useState<string>();
    const [callName, callNameSet] = useState<string>();

    const requestCall = (number?: string, name?: string) => {
        if (!number) return;
        callNumberSet(number);
        callNameSet(name);
    };

    const phoneDisplay = (p?: PhoneItem) => p?.phoneFriendly ?? p?.phone;
    const phoneDial = (p?: PhoneItem) => p ? ((p.countryCode ?? "") + (p.phone ?? "")) : undefined;

    // A real phone must contain enough digits. Numbers like "+1" (country code only) are ignored.
    const isRealPhone = (num?: string) => (num ?? "").replace(/\D/g, "").length >= 7;

    // Renders a phone row (icon + value) as click-to-call when the phone is ready.
    // Returns null when the number is missing or not a real phone.
    const renderPhoneRow = (display?: string, dialNum?: string, name?: string) => {
        if (!isRealPhone(dialNum ?? display)) return null;
        return (
            <FlexRow className="text-gray-700">
                <Icon icon="phone" className="me-2" size={10} type="solid" />
                {
                    phoneReady && dialNum ?
                        <a href="#" className="fs-8" title="Call this number"
                            onClick={e => {
                                e.preventDefault();
                                requestCall(dialNum, name);
                            }}>
                            {display}
                        </a>
                        :
                        <span className="fs-8">{display}</span>
                }
            </FlexRow>
        );
    };

    const openInvoice = (data: JobInvoice) => {
        if (!data.id || !data.jobId) return;
        selectedRowSet(data);
        drawerJobSet(undefined);
        jobLoadingSet(true);
        invoiceDrawerSet(true);

        hub.Get(API_ROUTES.Jobs + data.jobId, res => {
            if (res.success) drawerJobSet(res.data);
            jobLoadingSet(false);
        });
    };

    const closeInvoiceDrawer = () => {
        invoiceDrawerSet(false);
        selectedRowSet(undefined);
        drawerJobSet(undefined);
        // refresh the table so any changes made inside the drawer are reflected
        refreshSet(true);
    };

    // After creating a new invoice/estimate: close the small drawer, open the big view drawer
    // (the freshly created, item-less invoice auto-opens its Add Item modal).
    const handleCreated = (invoice: JobInvoice) => {
        createDrawerSet(false);
        refreshSet(true);
        if (invoice?.id && invoice?.jobId) openInvoice(invoice);
    };

    const CreateNewButton = () => (
        <Button text="Create New" icon="plus" onClick={() => createDrawerSet(true)} />
    );

    // Estimate-only row actions (confirm / undo confirm / convert to invoice).
    const EstimateActions = (data: JobInvoice) => {
        if (data.issued) return <></>;
        return (
            <>
                {
                    !data.confirmed &&
                    <div className="menu-item">
                        <a href="#" className="menu-link px-3 text-success"
                            onClick={e => {
                                e.preventDefault();
                                actionRowSet(data);
                                confirmModalSet(true);
                            }}>
                            <Icon icon="check-circle" className="me-2" />
                            Confirm
                        </a>
                    </div>
                }
                {
                    data.confirmed &&
                    <div className="menu-item">
                        <a href="#" className="menu-link px-3 text-warning"
                            onClick={e => {
                                e.preventDefault();
                                actionRowSet(data);
                                undoConfirmModalSet(true);
                            }}>
                            <Icon icon="circle-exclamation" className="me-2" />
                            Undo Confirm
                        </a>
                    </div>
                }
                {
                    data.confirmed &&
                    <div className="menu-item">
                        <a href="#" className="menu-link px-3"
                            onClick={e => {
                                e.preventDefault();
                                actionRowSet(data);
                                estToInvModalSet(true);
                            }}>
                            <Icon icon="file-invoice-dollar" className="me-2" />
                            Create Invoice
                        </a>
                    </div>
                }
            </>
        );
    };

    const TypeItem = (data: JobInvoice) => (
        <div>
            <span
                className={"badge fw-normal cursor-pointer " + (data.issued ? "badge-light-success" : "badge-light-primary")}
                onClick={() => openInvoice(data)}>
                <Icon icon={data.issued ? "file-invoice-dollar" : "file-invoice"} className="me-2" />
                {data.issued ? "Invoice" : "Estimate"} {data.number}
            </span>
        </div>
    )

    const JobItem = (data: JobInvoice) => (
        <div>
            <div>
                {
                    data.jobId ?
                        <a href="#"
                            onClick={e => {
                                e.preventDefault();
                                if (data.jobId) openJobQuickView(data.jobId);
                            }}>
                            #{data.jobDisplayId ?? "View"}
                        </a>
                        :
                        <span className="text-muted">—</span>
                }
                {
                    (data.jobFullName ?? data.billingName) &&
                    <span className="ms-2">
                        {data.jobFullName ?? data.billingName}
                    </span>
                }
            </div>
            {
                data.jobStatus &&
                <FlexRow className="mt-1">
                    <Icon icon="circle" type="solid" color={data.jobStatus.color} className="me-2" size={10} />
                    <span className="fs-8">{data.jobStatus.name}</span>
                </FlexRow>
            }
        </div>
    )

    const ContactItem = (data: JobInvoice) => {
        const custName = data.jobFullName ?? data.billingName;
        const display = phoneDisplay(data.jobPhone) ?? data.billingPhone?.phoneFriendly ?? data.billingPhone?.phone;
        const dialNum = phoneDial(data.jobPhone) ?? phoneDial(data.billingPhone);
        const address = data.jobAddress ?? data.billingAddress;
        return (
            <div>
                {
                    address &&
                    <FlexRow className="fs-8 mb-1 text-gray-600">
                        <Icon icon="location-dot" className="me-2" size={10} type="solid" />
                        <div className="fs-8">
                            {address}
                        </div>
                    </FlexRow>
                }
                {renderPhoneRow(display, dialNum, custName)}
            </div>
        );
    }

    const TechItem = (data: JobInvoice) => (
        <div>
            <div>
                {data.tech?.fullName}
            </div>
            <div className="mt-1">
                {renderPhoneRow(data.tech?.phoneNumber, data.tech?.phoneNumber, data.tech?.fullName)}
            </div>
        </div>
    )

    const TotalItem = (data: JobInvoice) => (
        <div>
            {moneyFormatter.format(data.total ?? 0)}
        </div>
    )

    const CreatedItem = (data: JobInvoice) => (
        <div>
            <div className="d-flex align-items-center">
                {data.createDate}
            </div>
            {
                data.creatorUser &&
                <FlexRow className="fs-8 mt-1">
                    <Icon icon="user" className="me-2" size={10} type="solid" />
                    <div className="fs-8">
                        By: {data.creatorUser.fullName}
                    </div>
                </FlexRow>
            }
        </div>
    )

    const StatusItem = (data: JobInvoice) => (
        <div>
            {
                data.lastSend &&
                <div className="mb-1">
                    <span className="badge badge-light-primary fw-normal me-1">
                        <Icon icon="share" className="me-2" />
                        Sent: {data.lastSend.sendDate}
                    </span>
                </div>
            }
            {
                data.viewsLog && data.viewsLog.length > 0 &&
                <div className="mb-1">
                    <span className="badge badge-light-primary fw-normal">
                        <Icon icon="eye" className="me-2" />
                        Viewed: {toPrettyDate(data.viewsLog[data.viewsLog.length - 1].createdAt)}
                    </span>
                </div>
            }
            {
                data.acceptLog &&
                <div className="mb-1">
                    <span className="badge badge-light-success fw-normal me-1">
                        <Icon icon="check-circle" className="me-2" />
                        Accepted: {toPrettyDate(data.acceptLog.createdAt)}
                    </span>
                </div>
            }
            {
                data.confirmed &&
                <div className="mb-1">
                    <span className="badge badge-light-success fw-normal me-1">
                        <Icon icon="check" className="me-2" />
                        Confirmed: {data.confirmDate}
                    </span>
                </div>
            }
            {
                data.paid &&
                <div className="mb-1">
                    <span className="badge badge-light-success fw-normal me-1">
                        <Icon icon="circle-dollar" className="me-2" />
                        Paid: {toPrettyDate(data.paidAt)}
                    </span>
                </div>
            }
        </div>
    )

    return (
        <>
            <DataTable apiRoute={API_ROUTES.JobInvoices}
                objectName="Invoice"
                preventCreate
                refresh={refresh}
                filteringProps={filteringProps}
                headButtons={CreateNewButton}
                edit
                editText="Open"
                editIcon="up-right-from-square"
                editCustom={(data: JobInvoice) => openInvoice(data)}
                delete
                canDelete={(data: JobInvoice) => !data.paid}
                onSend={(data: JobInvoice) => {
                    actionRowSet(data);
                    sendModalSet(true);
                }}
                extraParams={[
                    { name: "all", value: "true" }
                ]}
            >
                <DataTableField title="Type / #" width={160}>
                    <TypeItem />
                </DataTableField>
                <DataTableField title="Job">
                    <JobItem />
                </DataTableField>
                <DataTableField title="Contact">
                    <ContactItem />
                </DataTableField>
                <DataTableField title="Technician">
                    <TechItem />
                </DataTableField>
                <DataTableField title="Total" width={120}>
                    <TotalItem />
                </DataTableField>
                <DataTableField title="Created">
                    <CreatedItem />
                </DataTableField>
                <DataTableField title="Status">
                    <StatusItem />
                </DataTableField>
                <DataTableCustomActions>
                    <EstimateActions />
                </DataTableCustomActions>
            </DataTable>

            {
                invoiceDrawer &&
                <Drawer id="drawer-invoice-view"
                    size="xlarge"
                    title={selectedRow ? ((selectedRow.issued ? "Invoice " : "Estimate ") + (selectedRow.number ?? "")) : "Invoice"}
                    onClose={closeInvoiceDrawer}>
                    {
                        (jobLoading || !drawerJob || !selectedRow?.id) ?
                            <Indicator className="py-20" />
                            :
                            <JobInvoiceDetails job={drawerJob} invoiceId={selectedRow.id} inDrawer />
                    }
                </Drawer>
            }

            {
                callNumber &&
                <CallConfirmModal phone={callNumber} name={callName}
                    onClose={() => {
                        callNumberSet(undefined);
                        callNameSet(undefined);
                    }} />
            }

            {
                createDrawer &&
                <JobInvoiceCreateDrawer
                    onClose={() => createDrawerSet(false)}
                    onCreated={handleCreated} />
            }

            {
                sendModal && actionRow &&
                <JobInvoiceSendDrawer
                    ids={[actionRow.id ?? ""]}
                    jobId={actionRow.jobId}
                    title={(actionRow.issued ? "Send Invoice " : "Send Estimate ") + (actionRow.number ?? "")}
                    buttonText={actionRow.issued ? "Send Invoice" : "Send Estimate"}
                    defaultEmail={actionRow.billingEmail}
                    defaultPhone={actionRow.billingPhone}
                    onClose={() => {
                        sendModalSet(false);
                        actionRowSet(undefined);
                    }}
                    onSent={() => refreshSet(true)} />
            }

            {
                confirmModal && actionRow &&
                <Modal id="modal-jobinvoices-confirm"
                    title={"Confirm Estimate " + (actionRow.number ?? "")}
                    buttonText="Confirm"
                    buttonColor="success"
                    open visible staticBackdrop
                    onClose={() => {
                        confirmModalSet(false);
                        actionRowSet(undefined);
                    }}
                    onSubmit={(body, callback) => {
                        hub.Post(API_ROUTES.JobInvoices + "confirmestimate", {
                            ...body,
                            jobId: actionRow.jobId,
                            id: actionRow.id
                        }, res => {
                            callback!(res);
                            if (res.success) refreshSet(true);
                        });
                    }}>
                    <div>Are you sure you want to confirm this estimate?</div>
                </Modal>
            }

            {
                undoConfirmModal && actionRow &&
                <Modal id="modal-jobinvoices-undoconfirm"
                    title={"Undo Confirm Estimate " + (actionRow.number ?? "")}
                    buttonText="Undo Confirm"
                    buttonColor="warning"
                    open visible staticBackdrop
                    onClose={() => {
                        undoConfirmModalSet(false);
                        actionRowSet(undefined);
                    }}
                    onSubmit={(body, callback) => {
                        hub.Post(API_ROUTES.JobInvoices + "undoconfirmestimate", {
                            ...body,
                            jobId: actionRow.jobId,
                            ids: actionRow.id
                        }, res => {
                            callback!(res);
                            if (res.success) refreshSet(true);
                        });
                    }}>
                    <div>Are you sure you want to unconfirm this estimate?</div>
                </Modal>
            }

            {
                estToInvModal && actionRow &&
                <Modal id="modal-jobinvoices-esttoinv"
                    title="Create Invoice"
                    buttonText="Create Invoice"
                    buttonColor="success"
                    open visible
                    onClose={() => {
                        estToInvModalSet(false);
                        actionRowSet(undefined);
                    }}
                    onSubmit={(body, callback) => {
                        hub.Post(API_ROUTES.JobInvoices + "esttoinv", {
                            id: actionRow.id
                        }, res => {
                            callback!(res);
                            if (res.success) {
                                estToInvModalSet(false);
                                refreshSet(true);
                                if (res.data) openInvoice(res.data);
                            }
                        });
                    }}>
                    <div>Do you want to create an invoice based on this estimate?</div>
                </Modal>
            }
        </>
    )
}
